import { useId, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ZebraPlaceholder, ZebraTemplate, ZebraVersion } from '@/api/labelPrintingApi'
import { useLabelPrintingMutations, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import {
  effectiveFromText,
  formatDay,
  sizeAndResolution,
  zebraTemplateStatus,
  zebraVersionStatus,
} from '@/lib/labelPrinting'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { StatusBanner, StatusChip, SwitchField, activeNote } from './shared'
import { ZplPreview } from './ZplPreview'

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className='text-muted-foreground'>{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

/**
 * One version, read only: what the label looks like, who changed it and why,
 * where each blank is filled from, and the layout code (folded away).
 */
function VersionDetails({
  version,
  template,
  registry,
}: {
  version: ZebraVersion
  template: ZebraTemplate
  /** undefined while the placeholder list is loading. */
  registry: ZebraPlaceholder[] | undefined
}) {
  const [showCode, setShowCode] = useState(false)
  const filledFrom = new Map((registry ?? []).map(p => [p.name, p.filledFrom]))
  // The same version can be open twice: in its own section and from the list.
  const codeId = useId()
  return (
    <div className='flex flex-col gap-4'>
      <ZplPreview
        body={version.body}
        labelStock={template.labelStock}
        resolution={template.resolution}
        placeholders={registry}
      />

      <dl className='grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm'>
        <Row label='Version'>v{version.version}</Row>
        <Row label='Effective from'>{effectiveFromText(version.effectiveDate)}</Row>
        <Row label='Changed by'>{version.changedBy}</Row>
        <Row label='Changed at'>{formatDay(version.changedAt) ?? '—'}</Row>
        <Row label='Reason'>{version.reason}</Row>
        {version.approvedBy && (
          <Row label='Approved by'>
            {version.approvedBy}
            {version.approvedAt && ` on ${formatDay(version.approvedAt)}`}
          </Row>
        )}
      </dl>

      <div className='flex flex-col gap-2'>
        <h5 className='text-sm font-medium'>Placeholders</h5>
        {version.placeholders.length === 0 ? (
          <p className='text-sm text-muted-foreground'>None. Every value on this label is fixed text.</p>
        ) : (
          <div className='rounded-lg border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Placeholder</TableHead>
                  <TableHead>Filled from</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {version.placeholders.map(name => (
                  <TableRow key={name}>
                    <TableCell className='font-mono text-xs'>{`{{${name}}}`}</TableCell>
                    <TableCell>{filledFrom.get(name) ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        <div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='-ml-3'
            aria-expanded={showCode}
            aria-controls={codeId}
            onClick={() => setShowCode(v => !v)}
          >
            {showCode ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
            {showCode ? 'Hide layout code' : 'Show layout code'}
          </Button>
        </div>
        {showCode && (
          <pre id={codeId} className='max-h-72 overflow-auto rounded-lg border bg-muted/40 p-3 font-mono text-xs'>
            {version.body}
          </pre>
        )}
      </div>
    </div>
  )
}

/**
 * One Zebra template: the version in use, the draft waiting for approval, and
 * every earlier version. Anyone can read it. A manager can approve the draft,
 * retire or reactivate the template, and open it for a change.
 */
export function ZebraTemplateDialog({
  template,
  canManage,
  onEdit,
  onClose,
  onDone,
}: {
  template: ZebraTemplate
  canManage: boolean
  onEdit: () => void
  onClose: () => void
  onDone: (message: string) => void
}) {
  const options = useLabelPrintingOptions()
  const registry = options.data?.placeholders
  const { approveZebraTemplate, updateZebraTemplate } = useLabelPrintingMutations()
  const [shownId, setShownId] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const live = template.liveVersion
  const next = template.nextVersion
  const draft = template.draftVersion
  const shown = shownId ? template.versions.find(v => v.id === shownId) : undefined

  function approve() {
    if (!draft) return
    setServerError(null)
    // Names the draft shown here: if it changed since, the server refuses (409).
    approveZebraTemplate.mutate(
      { id: template.id, payload: { versionId: draft.id, changedAt: draft.changedAt } },
      {
        onSuccess: () => onDone(`Approved “${template.name}” v${draft.version}.`),
        onError: err => setServerError(errorText(err, 'Could not approve the draft.')),
      },
    )
  }

  function setActive(active: boolean) {
    if (updateZebraTemplate.isPending) return
    setServerError(null)
    updateZebraTemplate.mutate(
      { id: template.id, payload: { active } },
      { onError: err => setServerError(errorText(err, 'Could not change the template.')) },
    )
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          <DialogDescription>{sizeAndResolution(template.labelStock, template.resolution)}</DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-5'>
          <div>
            <StatusChip {...zebraTemplateStatus(template)} />
          </div>

          {live && (
            <section className='flex flex-col gap-3'>
              <h4 className='text-sm font-semibold'>Version in use</h4>
              <VersionDetails version={live} template={template} registry={registry} />
            </section>
          )}
          {!live && !next && (
            <StatusBanner tone='info'>No approved version yet. Printers cannot use this template.</StatusBanner>
          )}

          {next && (
            <section className='flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50/40 p-4'>
              <h4 className='text-sm font-semibold'>
                v{next.version} takes effect on {effectiveFromText(next.effectiveDate)}
              </h4>
              <VersionDetails version={next} template={template} registry={registry} />
            </section>
          )}

          {draft && (
            <section className='flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4'>
              <h4 className='text-sm font-semibold'>Draft v{draft.version} waiting for approval</h4>
              <VersionDetails version={draft} template={template} registry={registry} />
              {canManage && template.active && (
                <div className='flex flex-col gap-2 border-t border-amber-200 pt-3'>
                  <p className='text-sm'>
                    Print one on a lab Zebra printer and check it before approving. Smart CGT cannot print Zebra labels
                    yet.
                  </p>
                  <div>
                    <Button type='button' disabled={approveZebraTemplate.isPending} onClick={approve}>
                      {approveZebraTemplate.isPending ? 'Approving…' : 'Approve'}
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}

          <section className='flex flex-col gap-2'>
            <h4 className='text-sm font-semibold'>Versions</h4>
            <ul className='flex flex-col divide-y rounded-lg border'>
              {template.versions.map(v => {
                const selected = v.id === shownId
                return (
                  <li key={v.id}>
                    <button
                      type='button'
                      aria-pressed={selected}
                      onClick={() => setShownId(selected ? null : v.id)}
                      className={cn(
                        'flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-muted/50',
                        selected && 'bg-muted',
                      )}
                    >
                      <span className='font-medium'>v{v.version}</span>
                      <StatusChip {...zebraVersionStatus(v, template)} />
                      <span className='text-muted-foreground'>
                        {formatDay(v.changedAt) ?? '—'} · {v.changedBy}
                      </span>
                      <span className='min-w-0 flex-1 truncate text-muted-foreground'>{v.reason}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
            {shown && (
              <div className='rounded-lg border p-4'>
                <VersionDetails key={shown.id} version={shown} template={template} registry={registry} />
              </div>
            )}
          </section>

          {canManage && (
            <SwitchField
              id='zebra-template-active'
              label='Active'
              checked={template.active}
              onCheckedChange={setActive}
              note={activeNote(template.active)}
            />
          )}

          {serverError && (
            <p role='alert' className='text-sm text-destructive'>
              {serverError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Close
          </Button>
          {canManage && template.active && (
            <Button type='button' onClick={onEdit}>
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
